/**
 * An abort signal that fires after `ms`, for a request that needs a deadline.
 * It stands in for AbortSignal.timeout, which does not exist before Safari 16,
 * a browserslist target. The request rejects with an AbortError, not a
 * TimeoutError. Call clear() once the request settles.
 *
 * @param {number} ms
 * @returns {{signal: AbortSignal, clear: function(): void}}
 */
export function createTimeoutSignal(ms) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), ms);

    return {
        signal: controller.signal,
        clear: () => clearTimeout(timer),
    };
}

// ApiClient token refresh ignores the request signal, so the deadline must also race the work.
export async function withDeadline(work, ms) {
    const timeout = createTimeoutSignal(ms);
    let onAbort;
    const deadline = new Promise((resolve, reject) => {
        onAbort = () => reject(Object.assign(new Error('The request timed out'), { name: 'AbortError' }));
        timeout.signal.addEventListener('abort', onAbort, { once: true });
    });
    try {
        return await Promise.race([work(timeout.signal), deadline]);
    } finally {
        timeout.clear();
        timeout.signal.removeEventListener('abort', onAbort);
    }
}
